/**
 * Authentication service
 * Custom JWT authentication system
 *
 * @author Claude
 */
import {
  LoginCredentials,
  LoginResponse,
  RegisterData,
  RegisterResponse,
  AcceptInviteResponse,
  VerifyEmailResponse,
  ForgotPasswordResponse,
  ResetPasswordResponse,
  TokenRefreshResponse,
} from '../types/auth';
import { createApiClient } from './api';

/**
 * Accept invitation and set password
 */
export const acceptInvite = async (
  token: string,
  password: string
): Promise<AcceptInviteResponse> => {
  const api = createApiClient();
  const response = await api.post<AcceptInviteResponse>('/auth/accept-invite', {
    token,
    password,
  });
  return response.data;
};

/**
 * Verify email address
 */
export const verifyEmail = async (token: string): Promise<VerifyEmailResponse> => {
  const api = createApiClient();
  const response = await api.post<VerifyEmailResponse>('/auth/verify-email', {
    token,
  });
  return response.data;
};

/**
 * Resend verification email
 */
export const resendVerification = async (
  authToken: string
): Promise<{ success: boolean; message: string }> => {
  const api = createApiClient(authToken);
  const response = await api.post<{ success: boolean; message: string }>(
    '/auth/resend-verification'
  );
  return response.data;
};

/**
 * Register a new user
 */
export const register = async (data: RegisterData): Promise<RegisterResponse> => {
  const api = createApiClient();
  const response = await api.post<RegisterResponse>('/auth/register', data);
  return response.data;
};

/**
 * Login user with credentials
 */
export const login = async (credentials: LoginCredentials): Promise<LoginResponse> => {
  const api = createApiClient();
  const response = await api.post<LoginResponse>('/auth/login', credentials);
  return response.data;
};

/**
 * Logout user
 */
export const logout = async (token: string): Promise<void> => {
  const api = createApiClient(token);
  await api.post('/auth/logout');
};

/**
 * Request password reset email
 */
export const forgotPassword = async (email: string): Promise<ForgotPasswordResponse> => {
  const api = createApiClient();
  const response = await api.post<ForgotPasswordResponse>('/auth/forgot-password', {
    email,
  });
  return response.data;
};

/**
 * Reset password with token
 */
export const resetPassword = async (
  token: string,
  password: string
): Promise<ResetPasswordResponse> => {
  const api = createApiClient();
  const response = await api.post<ResetPasswordResponse>('/auth/reset-password', {
    token,
    password,
  });
  return response.data;
};

/**
 * Refresh authentication token
 */
export const refreshToken = async (refreshToken: string): Promise<TokenRefreshResponse> => {
  const api = createApiClient();
  const response = await api.post<TokenRefreshResponse>('/auth/token/refresh', {
    refresh_token: refreshToken,
  });
  return response.data;
};

/**
 * Validate token
 */
export const validateToken = async (token: string): Promise<boolean> => {
  try {
    const api = createApiClient();
    await api.post('/auth/validate', { token });
    return true;
  } catch (error) {
    return false;
  }
};
